import { useEffect, useMemo } from "react";
import {
  TextField,
  DatagridConfigurable,
  NumberField,
} from "react-admin";
import salesmanIncentiveFilterList from "../../components/salesmanIncentives/filter/salesmanIncentiveFilterList";
import getFirstAndLastDateOfCurrentMonth from "../../utils/firstLastDate";
import DownloadAction from "../../components/tableListActions/DownloadActionNew";
import MyDatagridBody from "../../components/common/MyDatagridBody";
import SalesmanIncentiveFooterList from "../../components/salesmanIncentives/footer/SalesmanIncentiveFooterList";
import SalesmanIncentiveHeader from "../../components/salesmanIncentives/header/SalesmanIncentiveHeader";
import ManualReportList from "../../components/reports/ManualReportList";
import useCompanyScopedListFilters from "../../hooks/useCompanyScopedListFilters";

const [firstDate, lastDate] = getFirstAndLastDateOfCurrentMonth();

const SalesmanIncentiveList = () => {
  const { permissions, canSeeCompanyColumn, filterDefaultValues } =
    useCompanyScopedListFilters({
      start_date: firstDate,
      end_date: lastDate,
    });
  const hasCompanyColumn = canSeeCompanyColumn;

  useEffect(() => {
    const mainElement = document.querySelector("main.RaLayout-contentWithSidebar");
    if (mainElement) {
      mainElement.classList.add("only-for-salesman-incentive");
    }
    return () => {
      if (mainElement) {
        mainElement.classList.remove("only-for-salesman-incentive");
      }
    };
  }, []);

  const list = useMemo(
    () => (
      <ManualReportList
        title="mine.label.salesman_incentive"
        empty={false}
        actions={<DownloadAction />}
        filters={salesmanIncentiveFilterList(permissions)}
        sx={{
          width: "100%",
          overflowX: "hidden",
          "& .RaList-main": { width: "100%" },
          "& .RaList-content": { width: "100%", overflowX: "hidden" },
          "& .RaDatagrid-tableWrapper": {
            overflowX: "auto",
            overflowY: "auto",
            width: "100%",
          },
        }}
        // filterDefaultValues={{ status: 1 }}
        // queryOptions={{ meta: { sort: ["created_at DESC"] } }} // overide default sort
        filterDefaultValues={filterDefaultValues}
        requiredFilters={["start_date", "end_date"]}
      >
        <DatagridConfigurable
          body={<MyDatagridBody footer={<SalesmanIncentiveFooterList />} />}
          header={<SalesmanIncentiveHeader hasCompanyColumn={hasCompanyColumn} />}
          bulkActionButtons={false}
          sx={{
            "--si-company-width": "170px",
            "--si-salesman-width": "230px",
            "& .RaDatagrid-tableWrapper": {
              overflowX: "auto",
              overflowY: "auto",
              width: "100%",
            },
            "& .RaDatagrid-table": {
              minWidth: 2360,
              width: "fit-content",
            },
            "& .RaDatagrid-headerCell, & .RaDatagrid-rowCell": {
              whiteSpace: "nowrap",
            },
            "& .si-sticky-company": {
              position: "sticky !important",
              left: 0,
              zIndex: 5,
              minWidth: "var(--si-company-width)",
              width: "var(--si-company-width)",
              maxWidth: "var(--si-company-width)",
              borderRight: "1px solid rgba(224, 224, 224, 1)",
              backgroundColor: (theme) => theme.palette.background.paper,
            },
            "& .si-sticky-name": {
              position: "sticky !important",
              left: hasCompanyColumn ? "var(--si-company-width)" : 0,
              zIndex: 5,
              minWidth: "var(--si-salesman-width)",
              width: "var(--si-salesman-width)",
              maxWidth: "var(--si-salesman-width)",
              borderRight: "1px solid rgba(224, 224, 224, 1)",
              backgroundColor: (theme) => theme.palette.background.paper,
            },
            "& .RaDatagrid-thead .si-sticky-company, & .RaDatagrid-tbody .si-sticky-company, & .RaDatagrid-thead .si-sticky-name, & .RaDatagrid-tbody .si-sticky-name": {
              zIndex: 7,
              backgroundColor: (theme) => theme.palette.background.default,
            },
            "& .si-col-a, & .si-col-c": {
              minWidth: 130,
              width: 130,
              borderRight: "1px solid rgba(224, 224, 224, 1)",
            },
            "& .si-col-a-total, & .si-col-c-total": {
              minWidth: 140,
              width: 140,
              borderRight: "1px solid rgba(224, 224, 224, 1)",
            },
            "& .si-col-a-total, & .si-col-b, & .si-col-c-total": {
              borderRight: "1px solid rgba(224, 224, 224, 1)",
            },
            "& .si-col-b": {
              minWidth: 90,
              width: 90,
              borderRight: "1px solid rgba(224, 224, 224, 1)",
            },
            "& .si-col-penalty, & .si-col-net, & .si-col-final, & .si-col-ratio, & .si-col-factor": {
              borderRight: "1px solid rgba(224, 224, 224, 1)",
            },
            "& .si-col-penalty": {
              minWidth: 130,
              width: 130,
            },
            "& .si-col-net, & .si-col-final": {
              minWidth: 190,
              width: 190,
            },
            "& .si-col-ratio": {
              minWidth: 130,
              width: 130,
            },
            "& .si-col-factor": {
              minWidth: 90,
              width: 90,
            },
          }}
        >
          {hasCompanyColumn ? (
            <TextField
              source="company.alias"
              label="mine.label.company"
              headerClassName="si-sticky-company"
              cellClassName="si-sticky-company"
            />
          ) : null}
          <TextField
            source="name"
            label="mine.label.name"
            headerClassName="si-sticky-name"
            cellClassName="si-sticky-name"
          />
          <NumberField
            source="sale_net_g"
            label="A-G"
            headerClassName="si-col-a"
            cellClassName="si-col-a"
          />
          <NumberField
            source="sale_net_phm"
            label="A-PHM"
            headerClassName="si-col-a"
            cellClassName="si-col-a"
          />
          <NumberField
            source="sale_net_z"
            label="A-Z"
            headerClassName="si-col-a"
            cellClassName="si-col-a"
          />
          <NumberField
            source="sale_net_total"
            label="A-Total"
            headerClassName="si-col-a-total"
            cellClassName="si-col-a-total"
          />
          <TextField
            source="incentive_percentage_g"
            label="B-G"
            headerClassName="si-col-b"
            cellClassName="si-col-b"
          />
          <TextField
            source="incentive_percentage_phm"
            label="B-PHM"
            headerClassName="si-col-b"
            cellClassName="si-col-b"
          />
          <TextField
            source="incentive_percentage_z"
            label="B-Z"
            headerClassName="si-col-b"
            cellClassName="si-col-b"
          />
          <NumberField
            source="gross_incentive_g"
            label="C-G"
            headerClassName="si-col-c"
            cellClassName="si-col-c"
          />
          <NumberField
            source="gross_incentive_phm"
            label="C-PHM"
            headerClassName="si-col-c"
            cellClassName="si-col-c"
          />
          <NumberField
            source="gross_incentive_z"
            label="C-Z"
            headerClassName="si-col-c"
            cellClassName="si-col-c"
          />
          <NumberField
            source="gross_incentive_total"
            label="C-Total"
            headerClassName="si-col-c-total"
            cellClassName="si-col-c-total"
          />
          <NumberField
            source="penalty"
            label="Penalti (D)"
            headerClassName="si-col-penalty"
            cellClassName="si-col-penalty"
          />
          <NumberField
            source="net_incentive"
            label="Insentif - Penalti (E=C-D)"
            headerClassName="si-col-net"
            cellClassName="si-col-net"
          />
          <NumberField
            source="profit_ratio"
            label="Rasio Profit (F)"
            headerClassName="si-col-ratio"
            cellClassName="si-col-ratio"
            options={{
              style: "percent",
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }}
          />
          <NumberField
            source="profit_multiplier"
            label="F. Pengali (G)"
            headerClassName="si-col-factor"
            cellClassName="si-col-factor"
          />
          <NumberField
            source="final_incentive"
            label="Insentif Diterima (E*G)"
            headerClassName="si-col-final"
            cellClassName="si-col-final"
          />
        </DatagridConfigurable>
      </ManualReportList>
    ),
    [hasCompanyColumn, permissions, filterDefaultValues]
  );
  return list;
};

export default SalesmanIncentiveList;
